<template>
  <div>
    <div class="payment-preview-card">
      <!-- Header -->
      <div class="card-header">Preview Skema Pembayaran</div>

      <div class="card-body">
        <!-- Form -->
        <div class="form-row-custom">
          <!-- Start Rent -->
          <input-date
            :value="startRent"
            label="Start Rent"
            placeholder="dd/mm/yyyy"
            @change="startRent = $event"
          />

          <!-- Duration -->
          <input-text
            id="inputDuration"
            :value="duration || ''"
            label="Durasi Sewa"
            placeholder="Masukkan angka"
            show-suffix-label
            :custom-class="{ labelClass: 'm-0 mt-1' }"
            :parser="(value) => value.replace(/\D/g, '')"
            @change="duration = $event"
          >
            <template #suffix-label> Bulan </template>
          </input-text>

          <!-- Preview Button -->
          <button
            type="button"
            class="preview-button"
            @click="generatePreviewPayment"
          >
            Preview
          </button>
        </div>

        <div class="separator"></div>

        <!-- Payment Schedule -->
        <div v-if="previewPaymentData.length" class="payment-table">
          <!-- Header -->
          <div class="payment-row payment-header">
            <div>PERIODE DARI</div>
            <div>PERIODE SAMPAI</div>
            <div>PEMBAYARAN KE</div>
            <div>TANGGAL TAGIHAN</div>
            <div>NILAI</div>
          </div>

          <!-- Rows -->
          <div
            v-for="(item, index) in previewPaymentData"
            :key="index"
            class="payment-row payment-row-content"
          >
            <div>
              {{ formatDate(item.periodFrom) }}
            </div>

            <div>
              {{ formatDate(item.periodTo) }}
            </div>

            <div class="payment-number">
              {{ item.paymentNumber }} {{ item.isProrate ? " - Prorate" : "" }}
            </div>

            <div>
              {{ formatDate(item.invoiceDate) }}
            </div>

            <div>
              {{ formatRupiah(item.amount) }}
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="empty-state">
          Preview skema pembayaran tidak ditemukan
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { InputDate, InputText } from "@/components/Input";
import { alert } from "@/utils";

export default {
  name: "PaymentSchedulePreview",
  components: {
    InputDate,
    InputText,
  },
  props: {
    previewPaymentData: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      startRent: "",
      duration: null,
    };
  },

  methods: {
    /**
     * Generate payment schedule
     */
    generatePreviewPayment() {
      if (!this.startRent || !this.duration || this.duration < 1) {
        alert().fire({
          icon: "error",
          title: "Gagal",
          text: "Tanggal mulai sewa dan durasi sewa harus diisi!",
        });
        return;
      }
      this.$emit("generate-preview-payment", {
        startRent: this.startRent,
        duration: this.duration,
      });
    },

    /**
     * Parse yyyy-mm-dd without timezone problems
     */
    parseDate(dateString) {
      const [year, month, day] = dateString.split("-").map(Number);

      return new Date(year, month - 1, day);
    },

    /**
     * Format date to Indonesian
     *
     * Example:
     * 2023-03-06 -> 6 Mar 2023
     */
    formatDate(date) {
      const parsedDate = this.parseDate(date);
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "Mei",
        "Jun",
        "Jul",
        "Agu",
        "Sep",
        "Okt",
        "Nov",
        "Des",
      ];

      return `${parsedDate.getDate()} ${
        months[parsedDate.getMonth()]
      } ${parsedDate.getFullYear()}`;
    },

    /**
     * Format number to Indonesian Rupiah
     *
     * Example:
     * 2000000 -> Rp 2.000.000
     */
    formatRupiah(value) {
      return "Rp " + new Intl.NumberFormat("id-ID").format(value);
    },
  },
};
</script>

<style scoped>
button:focus {
  outline: none;
}

.payment-preview-card {
  /* width: 100%; */
  border: 1px solid #d9e0e7;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  color: #3d4652;
  margin: 20px 20px;
}

/* =========================
   HEADER
========================= */

.card-header {
  height: 34px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  background: #f7f8fa;
  border-bottom: 1px solid #d9e0e7;

  font-size: 16px;
  font-weight: 700;
}

/* =========================
   BODY
========================= */

.card-body {
  padding: 16px 14px 14px;
}

/* =========================
   FORM
========================= */

.form-row-custom {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr;
  align-items: end;
  gap: 15px;
}

.form-group-custom {
  display: flex;
  flex-direction: column;
}

.form-group-custom label {
  margin-bottom: 4px;

  font-size: 11px;
  font-weight: 600;
  color: #3f4854;
}

.input-wrapper {
  position: relative;
}

.form-control-custom {
  width: 100%;
  height: 30px;

  box-sizing: border-box;

  border: 1px solid #d5dce4;
  border-radius: 6px;

  background: #fff;

  padding: 0 11px;

  font-size: 12px;
  color: #495057;

  outline: none;
}

.form-control-custom:focus {
  border-color: #2684ff;
}

.form-control-custom::placeholder {
  color: #adb5bd;
}

/* Remove default date appearance slightly */
input[type="date"] {
  color: #495057;
}

.calendar-icon {
  position: absolute;
  right: 8px;
  top: 50%;

  transform: translateY(-50%);

  font-size: 14px;
  pointer-events: none;
}

/* =========================
   DURATION
========================= */

.duration-input {
  display: flex;
}

.duration-input input {
  border-radius: 6px 0 0 6px;
}

.duration-label {
  height: 30px;
  min-width: 48px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  border: 1px solid #d5dce4;
  border-left: 0;

  border-radius: 0 6px 6px 0;

  background: #f7f8fa;

  font-size: 11px;
  color: #66717f;
}

/* =========================
   BUTTON
========================= */

.preview-button {
  height: 40px;
  padding: 0 15px;

  border: 0;
  border-radius: 6px;

  background: #2678e8;
  color: #fff;

  font-size: 14px;
  font-weight: 600;

  cursor: pointer;
}

.preview-button:hover {
  background: #1769d5;
}

/* =========================
   SEPARATOR
========================= */

.separator {
  height: 1px;
  background: #e0e5ea;

  margin: 14px 0;
}

/* =========================
   DIV TABLE
========================= */

.payment-table {
  width: 100%;

  border: 1px solid #d9e0e7;
  border-radius: 6px;

  overflow: hidden;
}

/*
  Instead of <table>, each row is a CSS Grid.

*/
.payment-row {
  display: grid;

  grid-template-columns:
    1fr
    1fr
    1fr
    1fr
    1fr;

  min-height: 30px;

  border-bottom: 1px solid #d9e0e7;
}

.payment-row:last-child {
  border-bottom: none;
}

.payment-row > div {
  display: flex;
  align-items: center;

  padding: 0 8px;

  border-right: 1px solid #d9e0e7;

  font-size: 12px;
}

.payment-row > div:last-child {
  border-right: none;
}

/* Header */

.payment-header {
  min-height: 29px;

  background: #f7f8fa;

  font-size: 12px;
  font-weight: 700;
  color: #6b7683;

  text-transform: uppercase;
}

.payment-header > div {
  justify-content: flex-start;
  white-space: nowrap;
}

.payment-header > div:nth-child(3),
.payment-header > div:nth-child(4),
.payment-header > div:nth-child(5) {
  justify-content: right;
}

/* Payment number */

.payment-number {
  justify-content: center;

  color: #1680ff;
  font-weight: 600;
}

/* Amount */

.payment-row > div:last-child {
  justify-content: flex-end;
}

.payment-row-content > div:nth-child(3),
.payment-row-content > div:nth-child(4),
.payment-row-content > div:nth-child(5) {
  display: flex;
  justify-content: flex-end;
}

/* =========================
   EMPTY
========================= */

.empty-state {
  padding: 30px 10px;

  text-align: center;

  font-size: 14px;
  color: #929ba6;
}
</style>


// WEBPACK FOOTER //
// src/components/Order/CustomComponent/PreviewPayment.vue